import { InfoAction } from 'src/app/model/map-actions/info-action';
import { Component, OnInit, ElementRef, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef, AfterViewInit, HostBinding } from '@angular/core';
import { DisplayAction } from 'src/app/model/map-actions/display-actions';
import { SLLayer, LayerAccessLevel } from 'src/app/model/sl-layer';
import { IconButtonComponent } from '../icon-button/icon-button.component';
import { ViewStackComponent } from '../view-stack/view-stack.component';
import { AttCastingViewComponent, CastingValue } from '../att-casting-view/att-casting-view.component';
import { AttributeInfo } from 'src/app/types/@types/sl-v-types';
import { UUID } from 'angular2-uuid';
import { ModalMessagerService } from 'src/app/modal-messager/modal-messager.service';
import { VocabularyPreferences } from '../att-vocab-view/att-vocab-view.component';
import { AttributesService, AttStorageType } from 'src/app/model/api-services/attributes.service';
import { LayerAttributesViewComponent } from '../layer-attributes-view/layer-attributes-view.component';
import { EnvService } from 'src/app/env.service';
import { SlWapiService } from 'src/app/model/api-services/sl-wapi.service';

export interface ILayerAttributeSubView {
  SetAttribute(layer: SLLayer, attribute: any): void;
}

@Component({
  selector: 'app-layer-attribute-view',
  templateUrl: './layer-attribute-view.component.html',
  styleUrls: ['./layer-attribute-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LayerAttributeViewComponent implements AfterViewInit {
  public id: string;
  public name = 'Attribute:';
  private layer: SLLayer;
  private attribute: AttributeInfo;
  private originalProtectedSettings: {
    hasVisible: boolean;
    visible: any;
    hasSearchable: boolean;
    searchable: any;
  };
  public searchIcon = 'search-plus';
  public attributeName = '';
  public attributeAlias = '';
  public attributeDescription = '';
  public attributeDataType = '';


  private _attributeSearchable = true;
  public set attributeSearchable(isOn: boolean) {
    this._attributeSearchable = isOn;
    this.Refresh();
  }
  public get attributeSearchable(): boolean {
    return this._attributeSearchable;
  }

  public hasConditions = false;
  public authOnly = false;

  public entryQualifierName = '';
  public entryConditions: any = null;
  public lastVocab: VocabularyPreferences = null;
  public lastEntryQualifier: CastingValue;
  public lastDisplayQualifier: CastingValue;
  public isEditable = false;
  public get canToggleAttributeFlags(): boolean {
    const username = SlWapiService.SLSESSION?.username;
    return !!username && !!this.layer && username === this.layer.ownerName;
  }



  @ViewChild('searchableBtn', { static: true }) searchableBtn: IconButtonComponent;
  @ViewChild('visibilityBtn', { static: true }) visibilityBtn: IconButtonComponent;
  @ViewChild('dataEntryVisBtn', { static: true }) dataEntryVisBtn: IconButtonComponent;
  @ViewChild('reportingVisBtn', { static: true }) reportingVisBtn: IconButtonComponent;
  @ViewChild('views', { static: true }) views: ViewStackComponent;
  @ViewChild('workArea', { static: true }) workArea: ElementRef;

  private _ready = false;
  private _attributeVisible = true;
  public set attributeVisible(isOn: boolean) {
    this._attributeVisible = isOn;
    const element: HTMLElement = this.domNode.nativeElement;
    element.classList.toggle('attribute-visible', this.attributeVisible === true);
    this.Refresh();
  }
  public get attributeVisible(): boolean {
    return this._attributeVisible;
  }

  private _isExportable = false;
  public set isExportable(isOn: boolean) {
    this._isExportable = isOn;
    this.domNode.nativeElement.classList.toggle('exportable', this._isExportable === true);
    this.Refresh();
  }
  public get isExportable(): boolean {
    return this._isExportable;
  }

  private _reportingVisible = false;
  @HostBinding('class.has-reporting')
  public set reportingVisible(isOn: boolean) {
    this._reportingVisible = isOn;
    this.Refresh();
  }
  public get reportingVisible(): boolean {
    return this._reportingVisible;
  }


  private _attributeDataEntryVisible = false;
  public set attributeDataEntryVisible(isOn: boolean) {
    this._attributeDataEntryVisible = isOn;
    this.domNode.nativeElement.classList.toggle('has-data-entry', this.attributeDataEntryVisible === true);
    this.Refresh();
  }
  public get attributeDataEntryVisible(): boolean {
    return this._attributeDataEntryVisible;
  }
  private _hasReportingQualifier = false;

  private _hasEntryQualifiers = false;
  @HostBinding('class.has-entry-qualifier')
  public set hasEntryQualifiers(isOn: boolean) {
    this._hasEntryQualifiers = isOn;
    if (this._hasEntryQualifiers === false) {
      if (!!this.attribute.casting) {
        if (!!this.attribute.casting.entry) {
          this.lastEntryQualifier = this.attribute.casting.entry;
          delete this.attribute.casting.entry;
        }
      }
    } else if (!!this.lastEntryQualifier) {
      if (!this.attribute.casting) {
        this.attribute.casting = { entry: this.lastEntryQualifier };
      } else {
        this.attribute.casting.entry = this.lastEntryQualifier;
      }
    }
    this.Refresh();
  }
  public get hasEntryQualifiers() { return this._hasEntryQualifiers; }

  @HostBinding('class.has-report-qualifier')
  public set hasReportingQualifier(isOn: boolean) {
    this._hasReportingQualifier = isOn;
    this.Refresh();
  }
  public get hasReportingQualifier(): boolean {
    return this._hasReportingQualifier;
  }

  private _requireAuth = false;
  @HostBinding('class.auth-required')
  public set requireAuth(isOn: boolean) {
    this._requireAuth = isOn;
    this.Refresh();
  }
  public get requireAuth(): boolean {
    return this._requireAuth;
  }

  private _hasVocab = false;
  @HostBinding('class.has-vocab')
  public set hasVocab(isOn: boolean) {
    this._hasVocab = isOn;
    if (!!this.attribute.vocab) {
      if (this._hasVocab === false) {
        this.lastVocab = this.attribute.vocab;
        delete this.attribute.vocab;
      }
    } else {
      if (this._hasVocab === true) {
        this.attribute.vocab = this.lastVocab;
      }
    }
    this.Refresh();
  }
  public get hasVocab(): boolean {
    return this._hasVocab;
  }

  constructor(private domNode: ElementRef, private messager: ModalMessagerService,
    private cdr: ChangeDetectorRef,
    private attSvc: AttributesService, private Env: EnvService) { }

  ngAfterViewInit(): void {
    this._ready = true;
    this.attributeSearchable = false;
    this.attributeVisible = false;
    this.attributeDataEntryVisible = false;
    this.Refresh();

  }


  ShowAttribute(layer: SLLayer, attribute: AttributeInfo) {
    if (!!attribute.id) {
      this.id = attribute.id;
    } else {
      this.id = UUID.UUID();
    }

    this.layer = layer;
    this.attribute = attribute;
    this.originalProtectedSettings = {
      hasVisible: Object.prototype.hasOwnProperty.call(attribute, 'visible'),
      visible: attribute.visible,
      hasSearchable: Object.prototype.hasOwnProperty.call(attribute, 'searchable'),
      searchable: attribute.searchable
    };
    if (this.Env.IsPublic() === true) {
      this.views.SetView('read')
      this.isEditable = false;
    } else if (this.layer.access === LayerAccessLevel.Edit) {
      this.views.SetView('editable');
      this.isEditable = true;
    } else {
      this.views.SetView('read');
      this.isEditable = false;
    }
    this.name = 'Attribute: <span class=\"fas fa-at\"></span> ' + this.attribute.display;
    this.attributeName = this.attribute.name;
    this.attributeAlias = this.attribute.display;
    this.attributeDescription = (!!this.attribute.description) ? this.attribute.description : '';
    this.attributeDataType = this.attribute.type;
    this.attributeSearchable = (this.attribute.searchable !== false);

    this.searchableBtn.Toggle(this.attribute.searchable, false);
    switch (this.attribute.type) {
      case 'string':
        this.attributeDataType = 'text';
        break;
    }
    this.attributeVisible = Number(this.attribute.visible) === 1;

    this.lastVocab = !!this.attribute.vocab ? this.attribute.vocab : null;
    this.hasVocab = !!this.attribute.vocab;
    if (this.hasVocab) {
      this.lastVocab = this.attribute.vocab;
    } else {
      this.lastVocab = null;
    }
    this._hasEntryQualifiers = false;
    this._hasReportingQualifier = false;
    this.lastEntryQualifier = null;
    if (this.attribute.casting) {
      if (!!this.attribute.casting.entry) {
        this.hasEntryQualifiers = true;
        this.lastEntryQualifier = this.attribute.casting.display;
      }
      if (!!this.attribute.casting.display) {
        this.hasReportingQualifier = true;
        this.lastDisplayQualifier = this.attribute.casting.display;
      }
    }

    this.hasVocab = this.hasVocab
    this.attributeDataEntryVisible = (this.hasVocab || this.hasEntryQualifiers);
    this.isExportable = (!!this.attribute.export) ? this.attribute.export.allow : false;
    this.reportingVisible = this.hasReportingQualifier || this.isExportable;
    this.attributeVisible = Number(this.attribute.visible) === 1;
    this.Refresh();

  }


  ToggleConditions(hasConditions: boolean) {
    this.hasConditions = hasConditions;
    this.domNode.nativeElement.classList.toggle('has-conditions', hasConditions === true);
    this.Refresh();
  }


  ToggleHasEntryQualifiers(hasQualifiers: boolean) {

  }
  GetEntryConditions(): string {
    return '';
  }
  Back() {
    DisplayAction.SetPrimaryView('layer-attributes-view');
  }
  public UpdateHeight(primaryHeight: number, panelMax: number): boolean {
    primaryHeight = primaryHeight;
    const element: HTMLElement = this.domNode.nativeElement;
    const title = element.querySelector('.panel-title') as HTMLElement;
    const actions = element.querySelector('.panel-actions') as HTMLElement;
    const fixedHeight = (title?.offsetHeight || 0) + (actions?.offsetHeight || 0);
    const contentHeight = fixedHeight + (this.workArea?.nativeElement?.scrollHeight || 0);
    const height = Math.min(contentHeight, panelMax) + 'px';
    const changed = element.style.height !== height;
    element.style.height = height;
    this.Refresh();
    return changed;
  }
  ConfigVocab(): void {
    const view = DisplayAction.SetPrimaryView('att-vocab-view') as ILayerAttributeSubView;
    if (!!view) {
      if (!!view.SetAttribute) {
        view.SetAttribute(this.layer, this.attribute);
      }
    }
    this.Refresh();
  }
  ConfigConditons(): void {
    const view = DisplayAction.SetPrimaryView('att-condition-view') as ILayerAttributeSubView;
    if (!!view) {
      if (!!view.SetAttribute) {
        view.SetAttribute(this.layer, this.attribute)
      }
    }
    this.Refresh();
  }
  ConfigReportQualifier(): void {
    const view = DisplayAction.SetPrimaryView('att-casting-view') as ILayerAttributeSubView;
    if (!!view) {
      if (!!view.SetAttribute) {
        ((view as any) as AttCastingViewComponent).SetAttribute(this.layer, this.attribute, 'display');
      }
    }
    this.Refresh();
  }
  ConfigEntryQualifier(): void {
    const view = DisplayAction.SetPrimaryView('att-casting-view') as ILayerAttributeSubView;
    if (!!view) {
      if (!!view.SetAttribute) {
        ((view as any) as AttCastingViewComponent).SetAttribute(this.layer, this.attribute, 'entry');
      }
    }
    this.Refresh();

  }
  Continue() {
    const message:string = 'Continuing will make changes locally but will not save them to the server until attributes for this layer are uploaded to the server. If you want to return to the attributes view without committing changes, click cancel below and use the red x button in the upper left of the panel to return to the attributes view.';
    this.messager.Confirm(message, () => {
      if (this.attribute.name !== this.attributeName) {
        this.attribute.newName = this.attributeName;
      }
        this.attribute.type = this.attributeDataType as AttStorageType;
        this.attribute.display = this.attributeAlias;
        this.attribute.description = this.attributeDescription;
        if (this.canToggleAttributeFlags) {
          this.attribute.visible = '' + Number(this.attributeVisible);
          this.attribute.searchable = this.attributeSearchable;
        } else {
          if (this.originalProtectedSettings.hasVisible) {
            this.attribute.visible = this.originalProtectedSettings.visible;
          } else {
            delete this.attribute.visible;
          }
          if (this.originalProtectedSettings.hasSearchable) {
            this.attribute.searchable = this.originalProtectedSettings.searchable;
          } else {
            delete this.attribute.searchable;
          }
        }
        if (this.attributeDataEntryVisible === false) {
          if (!!this.attribute.casting) {
            if (!!this.attribute.casting.entry) {
              delete this.attribute.casting.entry;
            }
          }
          if (!!this.attribute.vocab) {
            delete this.attribute.vocab;
          }
          if (!!this.attribute.conditions) {
            delete this.attribute.conditions;
          }

        }
        if (this.reportingVisible === false) {
          if (!!this.attribute.export) {
            this.attribute.export.allow = false;
            this.attribute.export.reqAuth = true;
          }
          if (!!this.attribute.casting) {
            if (!!this.attribute.casting.display) {
              delete this.attribute.casting.display;

            }
          }
        }
        /*let hadMatch = false;
        for(const attribute of this.layer.layerAttributes ) {
          let i=0;
          if(attribute.id === this.attribute.id) {
            this.layer.layerAttributes[i] = this.attribute;
            hadMatch = true;
            break;
          }
          i++;
        }
        if(!hadMatch) {
          this.layer.layerAttributes.push(this.attribute);
        }
        */
        const view = DisplayAction.SetPrimaryView('layer-attributes-view');
        if (view !== null) {
          (view as LayerAttributesViewComponent).SetLayer(this.layer);
        }
        InfoAction.RefreshResultsLayer(this.layer.map.component, { layer: this.layer })
        this.Refresh();
      }, null, 'Before you go. ..');
  }
  Remove() {
    this.messager.Confirm('Removing this attribute is a permanent action and cannot be undone. Are you sure you want to do this?'
      , () => {
        this.attSvc.RemoveAttribute(this.layer, this.attribute.name).then(() => {
          for (let i = 0, l = this.layer.layerAttributes.length; i < l; i++) {
            if (this.layer.layerAttributes[i].name === this.attribute.name) {
              this.layer.layerAttributes.splice(i, 1);
              const view = DisplayAction.SetPrimaryView('layer-attributes-view');
              if (view !== null) {
                (view as LayerAttributesViewComponent).SetLayer(this.layer);
              }
              break;
            }
          }
        });
        this.Refresh();

      },
      null,
      'Proceed with caution ...');
  }
  Refresh() {
    if (this._ready) this.cdr.detectChanges();
  }
}
